<template>
  <div>
    <SoRow>
      <SoCol cols="8">
        <SoAlert
          title="Confirm with the customer"
          data-testid="confirm-with-customer-alert"
        >
          <ul class="ml-4 mt-2">
            <li>
              Ensure somebody aged 18 or over will be present for the whole
              installation, even if the meters are outside
            </li>
            <li>
              Ensure there is a clear working area around the meter(s) on the
              day of the appointment
            </li>
            <li>
              If the engineer can't access the property, meters, or main fuse,
              the appointment will need to be rescheduled
            </li>
            <li>
              Please let us know if you need to cancel or make any changes 48
              hours before the appointment date
            </li>
          </ul>
        </SoAlert>

        <SoAlert
          title="Check with the customer - meter reading frequency"
          type="info"
          class="mt-7"
          data-testid="meter-reading-frequency-alert"
        >
          <p class="mt-2">
            Choose how you'd like to view your energy usage in your account.
            Half-hourly readings give you a detailed breakdown throughout the
            day, helping you spot where to reduce waste and save money.
          </p>
          <p class="mt-2">
            With either option your smart meter will also send us half-hourly
            readings, which are strictly anonymous. This helps us balance
            electricity supply and demand, contributing to a greener grid – and
            helping to keep your energy costs down.
          </p>
          <p class="mt-2">
            You can change these settings at any time in your account.
          </p>
        </SoAlert>

        <SoRadioGroup
          v-model="meterReadingFrequency"
          :items="meterReadingFrequencyOptions"
          :rules="[rules.required]"
          class="mt-7 mb-n8"
        />
      </SoCol>

      <SoCol cols="4" class="summary_column">
        <div class="pl-8 pb-6" data-testid="appointment-summary-job">
          <p class="heading">Job type</p>
          <p v-for="title in extraTitles" :key="title">{{ title }}</p>
          <p>Smart meter installation</p>
          <p v-if="fuelTypeLabel">{{ fuelTypeLabel }}</p>
          <SoLink
            text="Edit"
            @click="flowManager?.goToStep('appointmentType')"
          ></SoLink>
        </div>
        <div
          class="pl-8 pb-6"
          data-testid="appointment-summary-installation-partner"
        >
          <p class="heading">Installation partner</p>
          <p>{{ installationPartner }}</p>
        </div>
        <div class="pl-8 pb-6" data-testid="appointment-summary-date">
          <p class="heading">Appointment date & time</p>
          <p>{{ appointmentDateAndTime }}</p>
          <SoLink
            text="Edit"
            @click="flowManager?.goToStep('chooseAppointmentSlot')"
          ></SoLink>
        </div>
        <div class="pl-8 pb-6" data-testid="appointment-summary-meter-details">
          <p class="heading">Meter details</p>
          <p
            v-for="(text, index) in meterDetailsText"
            :key="index"
            class="summary_column-text"
          >
            {{ text }}
          </p>
          <SoLink
            text="Edit"
            @click="flowManager?.goToStep('meterDetails')"
          ></SoLink>
        </div>
        <div
          class="pl-8 pb-6"
          data-testid="appointment-summary-engineer-access"
        >
          <p class="heading">Engineer access</p>
          <p
            v-for="(text, index) in engineerAccessText"
            :key="index"
            class="summary_column-text"
          >
            {{ text }}
          </p>
          <SoLink
            text="Edit"
            @click="flowManager?.goToStep('engineerAccess')"
          ></SoLink>
        </div>
      </SoCol>
    </SoRow>
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType, inject } from "vue"
  import SoCol from "@/components/SoCol.vue"
  import SoRow from "@/components/SoRow.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoLink from "@/components/SoLink.vue"
  import SoRadioGroup from "@/components/formControls/SoRadioGroup.vue"
  import {
    ImbpJobNotes,
    ImbpQuestionnaire,
  } from "@/flows/useCases/imbpBookingNew/types"
  import { APPOINTMENT_TYPE_TITLES } from "@/types/ImbpAppointmentTypes"
  import rules from "@/validators/rules"
  import { SmartReadFrequencies } from "@/models/consent/consent.types"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import {
    meterAppointmentTypeValues,
    MeterAppointmentType,
  } from "@/constants/imbp"

  export default defineComponent({
    name: "ImbpNewAppointmentSummary",
    components: { SoCol, SoRow, SoAlert, SoLink, SoRadioGroup },
    props: {
      modelValue: {
        type: String,
        default: null,
      },
      jobNotes: {
        type: Object as PropType<Partial<ImbpJobNotes>>,
        default: () => ({}),
      },
      appointmentDateAndTime: {
        type: String,
        default: null,
      },
      meterDetails: {
        type: Object as PropType<ImbpQuestionnaire>,
        default: () => ({}),
      },
      engineerAccess: {
        type: Object as PropType<ImbpQuestionnaire>,
        default: () => ({}),
      },
    },
    emits: ["update:modelValue"],
    // eslint-disable-next-line vue/component-api-style
    setup() {
      const flowManager = inject<{
        goToStep: (step: string) => void | undefined
      }>("flowManager")

      return { flowManager }
    },
    data() {
      return {
        current: useCurrentAccountStore(),
        SmartReadFrequencies,
        meterReadingFrequencyOptions: [
          {
            text: "Customer agrees to half-hourly readings (recommended)",
            value: SmartReadFrequencies.HALF_HOURLY,
          },
          {
            text: "Customer wants daily readings",
            value: SmartReadFrequencies.DAILY,
          },
        ],
        rules,
      }
    },
    computed: {
      meterReadingFrequency: {
        get() {
          return this.modelValue
        },
        set(value: string) {
          this.$emit("update:modelValue", value)
        },
      },
      meterDetailsText() {
        return Object.entries(this.meterDetails).reduce(
          (result, [key, value]) => {
            if (value.answer) {
              switch (key) {
                case "distanceBetweenMeters":
                  result.push(
                    `Meters are ${this.lowercaseFirst(
                      value.answer as string,
                    )} apart`,
                  )
                  break
                case "gasMeterLocation":
                  result.push(
                    `Gas meter is ${this.lowercaseFirst(
                      value.answer as string,
                    )}`,
                  )
                  break
                case "meterAboveHeadHeight":
                  result.push("Ladder required")
                  break
                case "lessThanSixInchesAroundMeter":
                  result.push("Less than 6 inches around meter")
                  break
                case "electricityMeterThreePhase":
                  result.push("Three-phase electric meter")
                  break
                case "electricityMeterFiveTerminal":
                  result.push("Five-terminal electric meter")
                  break
                case "gasMeterMediumPressureOrCommercial":
                  result.push("Gas meter large, medium-pressure, or commercial")
                  break
                default:
                  return result
              }
            }
            return result
          },
          [] as string[],
        )
      },
      engineerAccessText() {
        return Object.values(this.engineerAccess)
          .filter(({ answer }) => Boolean(answer))
          .map(({ answer }) => answer)
      },
      extraTitles(): string[] {
        return (this.jobNotes.extras ?? []).map(
          (extra) => APPOINTMENT_TYPE_TITLES[extra],
        )
      },
      fuelTypeLabel(): string | null {
        const FUEL_TYPE_LABELS: Record<MeterAppointmentType, string> = {
          [meterAppointmentTypeValues.DUAL]: "Dual fuel",
          [meterAppointmentTypeValues.ELEC_ONLY]: "Elec only",
          [meterAppointmentTypeValues.GAS_ONLY]: "Gas only",
        }
        const type =
          this.current.smartMeterEligibility.data?.meterInstallationType
        return type ? (FUEL_TYPE_LABELS[type] ?? null) : null
      },
      installationPartner(): string {
        const mop = this.current.smartMeterEligibility.data?.meterOperatorCode
        if (mop === "SMS" || mop === "AES") return mop
        return "AES"
      },
    },
    methods: {
      lowercaseFirst(str: string) {
        return str.charAt(0).toLowerCase() + str.slice(1)
      },
    },
  })
</script>

<style scoped>
  .summary_column {
    line-height: 1.8rem;
    white-space: nowrap;
  }

  .summary_column-text {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .heading {
    font-size: 18px;
    font-weight: var(--theme-font-weight-medium);
  }

  :deep(.radio-label-container span) {
    font-size: 18px;
  }
</style>
